import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";

const initialState = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  child_age: "",
  number_of_students: 1,
  interested_language: "Hausa",
  learner_type: "parent",
  preferred_start_date: "",
};

export default function WaitingListForm({ onSuccess, compact = false }) {
  const [form, setForm] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/waiting-list", {
        ...form,
        number_of_students: Number(form.number_of_students) || 1,
      });
      toast.success("You're on the list! We'll be in touch soon.");
      setDone(true);
      setForm(initialState);
      onSuccess?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="waiting-list-success"
        className="rounded-3xl border border-border bg-card p-8 text-center"
      >
        <CheckCircle2 className="mx-auto mb-3 h-10 w-10 text-primary" strokeWidth={1.5} />
        <h4 className="mb-2 text-2xl">You're on the list.</h4>
        <p className="text-muted-foreground">
          Karɓi sannu! We'll email you as soon as classes open.
        </p>
        <Button
          variant="outline"
          className="mt-6 rounded-full"
          onClick={() => setDone(false)}
          data-testid="add-another-waiting-list"
        >
          Add another
        </Button>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="waiting-list-form"
      className={`grid gap-4 ${compact ? "" : "md:grid-cols-2"}`}
    >
      <div>
        <Label htmlFor="wl-first">First name</Label>
        <Input
          id="wl-first"
          data-testid="wl-first-name"
          required
          value={form.first_name}
          onChange={(e) => update("first_name", e.target.value)}
          className="mt-1.5 rounded-xl"
        />
      </div>
      <div>
        <Label htmlFor="wl-last">Last name</Label>
        <Input
          id="wl-last"
          data-testid="wl-last-name"
          required
          value={form.last_name}
          onChange={(e) => update("last_name", e.target.value)}
          className="mt-1.5 rounded-xl"
        />
      </div>
      <div>
        <Label htmlFor="wl-email">Email</Label>
        <Input
          id="wl-email"
          type="email"
          data-testid="wl-email"
          required
          value={form.email}
          onChange={(e) => update("email", e.target.value)}
          className="mt-1.5 rounded-xl"
        />
      </div>
      <div>
        <Label htmlFor="wl-phone">Phone</Label>
        <Input
          id="wl-phone"
          data-testid="wl-phone"
          required
          value={form.phone}
          onChange={(e) => update("phone", e.target.value)}
          className="mt-1.5 rounded-xl"
        />
      </div>
      <div>
        <Label htmlFor="wl-age">Child age (if any)</Label>
        <Input
          id="wl-age"
          data-testid="wl-child-age"
          placeholder="e.g. 7"
          value={form.child_age}
          onChange={(e) => update("child_age", e.target.value)}
          className="mt-1.5 rounded-xl"
        />
      </div>
      <div>
        <Label htmlFor="wl-num">Number of students</Label>
        <Input
          id="wl-num"
          type="number"
          min="1"
          data-testid="wl-num-students"
          value={form.number_of_students}
          onChange={(e) => update("number_of_students", e.target.value)}
          className="mt-1.5 rounded-xl"
        />
      </div>
      <div>
        <Label>Interested language</Label>
        <Select
          value={form.interested_language}
          onValueChange={(v) => update("interested_language", v)}
        >
          <SelectTrigger data-testid="wl-language" className="mt-1.5 rounded-xl">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Hausa">Hausa</SelectItem>
            <SelectItem value="Yoruba">Yoruba (coming)</SelectItem>
            <SelectItem value="Igbo">Igbo (coming)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label htmlFor="wl-date">Preferred start date</Label>
        <Input
          id="wl-date"
          type="date"
          data-testid="wl-start-date"
          value={form.preferred_start_date}
          onChange={(e) => update("preferred_start_date", e.target.value)}
          className="mt-1.5 rounded-xl"
        />
      </div>
      <div className={`${compact ? "" : "md:col-span-2"} flex items-center gap-6 pt-1`}>
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            data-testid="wl-parent"
            checked={form.learner_type === "parent"}
            onCheckedChange={() => update("learner_type", "parent")}
          />
          Parent registering child
        </label>
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            data-testid="wl-adult"
            checked={form.learner_type === "adult"}
            onCheckedChange={() => update("learner_type", "adult")}
          />
          Adult learner
        </label>
      </div>
      <div className={compact ? "" : "md:col-span-2"}>
        <Button
          type="submit"
          disabled={loading}
          data-testid="wl-submit"
          className="w-full rounded-full bg-primary py-6 text-base font-medium text-primary-foreground hover:bg-primary/90"
        >
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Join the Waiting List
        </Button>
        <p className="mt-2 text-xs text-muted-foreground">
          No spam. We only email you about the launch and your place in line.
        </p>
      </div>
    </form>
  );
}
